'use client'

import { useCallback } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import clsx from 'clsx'
import { createParser, useQueryState } from 'nuqs'
import { useTranslation } from 'react-i18next'
import { ImageWithPlaceholder } from '@/components/ImageWithPlaceholder/ImageWithPlaceholder'
import { getImgSrc } from '@/services/storyblok/Storyblok.helpers'
import { makeAbsolute } from '@/utils/url'
import { Badge } from 'ui/src/components/Badge/Badge'
import { Button } from 'ui/src/components/Button/Button'
import { Text } from 'ui/src/components/Text/Text'
import { ChartIcon } from 'ui/src/icons/ChartIcon'
import { xStack, yStack } from 'ui/src/patterns/stack'
import { tokens } from 'ui/src/theme/tokens.css'
import { visuallyHidden } from 'ui/src/utils/visibility.css'
import { SignUpDialog } from '../../SignUpDialog/SignUpDialog'
import type { VintageShopProductTeaser } from '../../vintageShop.types'
import {
  cardActionsRow,
  fullWidthImageWrapper,
  fullWidthItem,
  handwrittenPriceImage,
  hiddenFromMd,
  hiddenUntilMd,
  mainLink,
  productCard,
  productGrid,
  productImage,
  productImageWrapper,
} from './VintageShopProductList.css'

const parseAsFlag = createParser({
  parse: () => true,
  serialize: () => 'true',
})
  .withDefault(false)
  .withOptions({ clearOnDefault: true })

type Props = {
  teasers: Array<VintageShopProductTeaser>
}

export function VintageShopProductList({ teasers }: Props) {
  const [isSignUpOpen, setIsSignUpOpen] = useQueryState('signup', parseAsFlag)

  const handleOpenSignUp = useCallback(() => setIsSignUpOpen(true), [setIsSignUpOpen])

  return (
    <>
      <div className={productGrid}>
        {teasers.map((item, index) => (
          <div key={item.id} className={item.fullWidthImage ? fullWidthItem : undefined}>
            <ProductCard teaser={item} priority={index < 4} onSignUp={handleOpenSignUp} />
          </div>
        ))}
      </div>
      <SignUpDialog open={isSignUpOpen} onOpenChange={setIsSignUpOpen} />
    </>
  )
}

type ProductCardProps = {
  teaser: VintageShopProductTeaser
  priority: boolean
  onSignUp: () => void
}

function ProductCard({ teaser, priority, onSignUp }: ProductCardProps) {
  const { t } = useTranslation(['vintageShop'])

  const isSold = teaser.isSold === true

  return (
    <div className={productCard}>
      {teaser.fullWidthImage ? (
        <>
          <div className={clsx(fullWidthImageWrapper, hiddenFromMd)}>
            <ImageWithPlaceholder
              src={getImgSrc(teaser.image.src)}
              alt={teaser.image.alt}
              fill={true}
              className={productImage}
              imagePriority={priority}
              sizes="100vw"
            />
          </div>
          <div className={clsx(fullWidthImageWrapper, hiddenUntilMd)}>
            <ImageWithPlaceholder
              src={getImgSrc(teaser.fullWidthImage.src)}
              alt={teaser.fullWidthImage.alt}
              fill={true}
              className={productImage}
              imagePriority={priority}
              sizes="100vw"
            />
          </div>
        </>
      ) : (
        <div className={productImageWrapper}>
          <ImageWithPlaceholder
            src={getImgSrc(teaser.image.src)}
            alt={teaser.image.alt}
            fill={true}
            className={productImage}
            imagePriority={priority}
            sizes="(min-width: 768px) 33vw, 100vw"
          />
        </div>
      )}
      <div className={yStack({ gap: 'none' })}>
        <div className={xStack({ gap: 'md' })}>
          <div className={yStack({ gap: 'none', flex: 1 })}>
            <Link className={mainLink} href={makeAbsolute(teaser.href)}>
              {teaser.heading}
            </Link>
          </div>
          <div className={yStack({ gap: 'none', alignItems: 'flex-end' })}>
            {teaser.currentPrice && (
              <Text size="md" strikethrough={true}>
                {teaser.currentPrice}
              </Text>
            )}
            {teaser.ourPrice &&
              (teaser.priceImage ? (
                <>
                  <Image
                    src={getImgSrc(teaser.priceImage.src)}
                    alt={teaser.ourPrice}
                    aria-hidden={true}
                    width={0}
                    height={0}
                    sizes="200px"
                    className={handwrittenPriceImage}
                  />
                  <span className={visuallyHidden}>{teaser.ourPrice}</span>
                </>
              ) : (
                <Text size="md">{teaser.ourPrice}</Text>
              ))}
          </div>
        </div>
        {teaser.priceIncrease && (
          <div className={xStack({ gap: 'xs' })}>
            <Text size="md" color="signalGreenElement">
              {teaser.priceIncrease}
            </Text>
            <ChartIcon color={tokens.colors.signal.green.element} />
          </div>
        )}
      </div>
      <div className={cardActionsRow}>
        {isSold && (
          <Badge size="large" variant="yellow3">
            {t('VINTAGE_SHOP_SOLD_LABEL')}
          </Badge>
        )}
        {!isSold && (
          <>
            <Button as="a" href={makeAbsolute(teaser.href)} variant="primary">
              {t('VINTAGE_SHOP_TO_PRODUCT')}
            </Button>
            <Button variant="secondary" onClick={onSignUp}>
              {t('VINTAGE_SHOP_SIGN_UP_BUTTON')}
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
